import { useMemo, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { setUserLocale } from "@/i18n";
import { getBlogLocaleSwitchPath } from "@/lib/blog";
import { SUPPORTED_LOCALES, type SupportedLocale } from "@/services/localeResolver";
import { AnimatePresence, motion } from "framer-motion";
import { FlagIcon } from "./FlagIcon";

type LocaleOption = {
  code: SupportedLocale;
  label: string;
};

const OPTIONS: LocaleOption[] = [
  { code: "hr", label: "HR" },
  { code: "en", label: "EN" },
  // { code: "de", label: "DE" },
  // { code: "es", label: "ES" },
  // { code: "fr", label: "FR" },
  // { code: "it", label: "IT" },
];

export const LocalePicker = ({ className = "" }: { className?: string }) => {
  const { i18n, t } = useTranslation(["common"]);
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const activeLocale = useMemo(() => {
    const code = (i18n.resolvedLanguage || i18n.language || "hr").toLowerCase();
    const matched = SUPPORTED_LOCALES.find((locale) => code.startsWith(locale));
    return matched ?? "hr";
  }, [i18n.language, i18n.resolvedLanguage]);

  const activeOption = OPTIONS.find(opt => opt.code === activeLocale) || OPTIONS[0];
  const shouldToggleDirectly = OPTIONS.length === 2;
  const localizedLandingLocale = location.pathname === "/hr" || location.pathname === "/en"
    ? location.pathname.slice(1) as SupportedLocale
    : null;

  const switchLocale = (locale: SupportedLocale) => {
    void setUserLocale(locale);
    const blogPath = getBlogLocaleSwitchPath(location.pathname, locale === "en" ? "en" : "hr");
    if (blogPath && blogPath !== location.pathname) {
      navigate(blogPath);
      return;
    }
    if (localizedLandingLocale && locale !== localizedLandingLocale) {
      navigate(`/${locale}`);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={containerRef} className={`relative ${className}`.trim()}>
      {/* Trigger — flag only, borderless */}
      <button
        type="button"
        onClick={() => {
          if (shouldToggleDirectly) {
            const nextOption = OPTIONS.find((option) => option.code !== activeLocale);
            if (nextOption) {
              switchLocale(nextOption.code);
            }
            return;
          }
          setIsOpen(!isOpen);
        }}
        aria-label={t("common:common.locale.switcher", { defaultValue: "Language" })}
        className="w-8 h-8 rounded-full flex items-center justify-center outline-none focus:ring-2 focus:ring-primary/40 transition-all hover:scale-110 active:scale-95 overflow-hidden"
      >
        <FlagIcon code={activeOption.code} className="w-7 h-5 rounded-sm" />
      </button>

      {/* Dropdown — compact flag pills */}
      <AnimatePresence>
        {!shouldToggleDirectly && isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.92 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
            className="absolute top-full right-0 mt-2 bg-background border border-border rounded-xl shadow-lg overflow-hidden z-50 p-1.5"
          >
            <div className="flex flex-col gap-0.5">
              {OPTIONS.map((option) => {
                const isActive = activeLocale === option.code;
                return (
                  <button
                    key={option.code}
                    type="button"
                    onClick={() => {
                      switchLocale(option.code);
                      setIsOpen(false);
                    }}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all ${
                      isActive
                        ? "bg-primary/10 text-primary font-semibold"
                        : "text-foreground hover:bg-secondary/60"
                    }`}
                  >
                    <FlagIcon code={option.code} className="w-7 h-5 rounded-sm flex-shrink-0" />
                    <span className="font-body text-xs font-medium min-w-[1.5rem]">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
